import { API } from '@/src/api/api'
import { queryClient } from '@/src/api/queryClient'
import { queryKeys } from '@/src/api/queryKeys'
import { ApiError } from '@/src/api/types/procurement'
import type {
  CustomReferenceBaseLoadControllerDeleteCustomReferenceBaseLoadData,
  CustomReferenceBaseLoadControllerUploadCustomReferenceBaseLoadData,
} from '@/src/api/types/procurement'
import { invalidateStrategyOverviewsQueries } from '@/src/features/procurement-and-markets/overview/energy/hooks/useStrategyOverviews.hook'
import { useMutation } from '@tanstack/react-query'

const invalidateCustomReferenceBaseLoadQueries = () =>
  Promise.all([
    queryClient.invalidateQueries({ queryKey: queryKeys.procurement.loadPlanning.toKey() }),
    queryClient.invalidateQueries({ queryKey: queryKeys.procurement.loadPlanningsV2.toKey() }),
    queryClient.invalidateQueries({ queryKey: queryKeys.procurement.loadAdjustments.toKey() }),
    queryClient.invalidateQueries({ queryKey: queryKeys.procurement.loadAdjustmentsV2.toKey() }),
    queryClient.invalidateQueries({ queryKey: queryKeys.procurement.plannedStrategies.toKey() }),
    queryClient.invalidateQueries({ queryKey: queryKeys.procurement.portfolio.toKey() }),
    queryClient.invalidateQueries({ queryKey: queryKeys.procurement.portfolioTimeseries.toKey() }),
    invalidateStrategyOverviewsQueries(),
  ])

export const getCustomReferenceBaseLoadUploadErrorMessage = (error: unknown): string | null => {
  if (!(error instanceof ApiError) || error.status !== 400) return null

  const body = error.body
  if (!body || typeof body !== 'object' || !('message' in body)) return null

  const message = body.message
  if (typeof message !== 'string') return null

  const text = message.trim()
  return text || null
}

export const useUploadCustomReferenceBaseLoad = () => {
  return useMutation({
    mutationFn: (data: CustomReferenceBaseLoadControllerUploadCustomReferenceBaseLoadData) => {
      return API.procurements.uploadCustomReferenceBaseLoad(data)
    },
    onSuccess: invalidateCustomReferenceBaseLoadQueries,
  })
}

export const useDeleteCustomReferenceBaseLoad = () => {
  return useMutation({
    mutationFn: (data: CustomReferenceBaseLoadControllerDeleteCustomReferenceBaseLoadData) => {
      return API.procurements.deleteCustomReferenceBaseLoad(data)
    },
    onSuccess: invalidateCustomReferenceBaseLoadQueries,
  })
}
